import { createPopper } from '@popperjs/core';
import { defineComponent, onMounted, watch, ref, onUnmounted, reactive, h } from '@vue/composition-api';
import { clickOutside } from '../directives/click-outside';
import { withInstall } from '../util';
import { getPlament } from './utils/rtl';
import { DarkArrowDown, DarkArrowLeft, DarkArrowRight, DarkArrowUp, LightArrowDown, LightArrowLeft, LightArrowRight, LightArrowUp } from './icon';
import "../style/components/tooltip.css";
var LightArrowMap = {
  top: LightArrowDown,
  'top-start': LightArrowDown,
  'top-end': LightArrowDown,
  bottom: LightArrowUp,
  'bottom-start': LightArrowUp,
  'bottom-end': LightArrowUp,
  left: LightArrowRight,
  'left-start': LightArrowRight,
  'left-end': LightArrowRight,
  right: LightArrowLeft,
  'right-start': LightArrowLeft,
  'right-end': LightArrowLeft
};
var DarkArrowMap = {
  top: DarkArrowDown,
  'top-start': DarkArrowDown,
  'top-end': DarkArrowDown,
  bottom: DarkArrowUp,
  'bottom-start': DarkArrowUp,
  'bottom-end': DarkArrowUp,
  left: DarkArrowRight,
  'left-start': DarkArrowRight,
  'left-end': DarkArrowRight,
  right: DarkArrowLeft,
  'right-start': DarkArrowLeft,
  'right-end': DarkArrowLeft
};
export var toolTipProps = {
  // 受控的popper可见性
  visible: {
    type: Boolean,
    "default": undefined
  },
  title: {
    type: String
  },
  placement: {
    type: String,
    "default": 'top'
  },
  autoPlacement: {
    type: Boolean,
    "default": false
  },
  theme: {
    type: String,
    "default": 'light'
  },
  tooltipClassName: {
    type: String
  },
  offset: {
    type: Array,
    "default": function _default() {
      return [0, 0];
    }
  },
  arrowOffset: {
    type: [Number, String],
    "default": function _default() {
      return 0;
    }
  },
  autoArrow: {
    type: Boolean,
    "default": function _default() {
      return false;
    }
  },
  preventOverflow: {
    type: Array,
    "default": function _default() {
      return [true, false];
    }
  },
  styles: {
    type: Object,
    "default": function _default() {}
  }
};
var ToolTip = defineComponent({
  name: 'm-tooltip',
  directives: {
    clickOutside: clickOutside
  },
  model: {
    prop: 'visible',
    event: 'change'
  },
  props: toolTipProps,
  slots: ['title'],
  emits: ['change'],
  setup: function setup(props, context) {
    var placement = getPlament(props.placement);
    var state = reactive({
      // 非受控的popper可见性
      visible: false,
      // 非受控的popper状态
      placement: placement
    });
    var popper = ref(); // popper自动调整placement后箭头也要跟着调整

    var onUpdateModifier = function onUpdateModifier(popper) {
      if (popper.state.placement !== state.placement) {
        state.placement = popper.state.placement;
      }
    };

    var getOptions = function getOptions(_ref) {
      var placement = _ref.placement,
          offset = _ref.offset,
          autoPlacement = _ref.autoPlacement,
          preventOverflow = _ref.preventOverflow,
          autoArrow = _ref.autoArrow;
      return {
        placement: placement,
        modifiers: [{
          name: 'offset',
          options: {
            offset: [0 + offset[0], 10 + offset[1]]
          }
        }, {
          name: 'updateModifier',
          enabled: true,
          phase: 'main',
          fn: onUpdateModifier
        }, {
          name: 'flip',
          options: {
            allowedAutoPlacements: ['top', 'bottom', 'left', 'right'],
            fallbackPlacements: autoPlacement ? ['top', 'bottom', 'left', 'right'] : []
          }
        }, {
          name: 'preventOverflow',
          options: {
            // 配置x轴的偏移
            mainAxis: preventOverflow[0]
          }
        }, {
          name: 'preventOverflow',
          options: {
            // 配置y轴的偏移
            altAxis: preventOverflow[1]
          }
        }, autoArrow ? {
          name: 'arrow',
          options: {
            element: context.refs.popperArrow
          }
        } : undefined].filter(function (isValidModifiers) {
          return isValidModifiers;
        })
      };
    };

    onMounted(function () {
      // 初始化popper
      popper.value = createPopper(context.refs.trigger, context.refs.popper, getOptions({
        placement: state.placement,
        offset: props.offset,
        autoPlacement: props.autoPlacement,
        preventOverflow: props.preventOverflow,
        autoArrow: props.autoArrow && context.refs.popperArrow
      }));
    });
    onUnmounted(function () {
      // 销毁popper
      if (popper.value) {
        popper.value.destroy();
      }
    });
    watch(function () {
      return [props.title];
    }, function () {
      // 更新popper
      if (popper.value) {
        popper.value.update();
      }
    }, {
      immediate: true
    });
    watch(function () {
      return [props.placement, props.offset, props.autoPlacement, props.preventOverflow, props.autoArrow];
    }, function () {
      if (props.placement !== state.placement) {
        state.placement = getPlament(props.placement);
      } // 更新popper配置


      if (popper.value) {
        popper.value.setOptions(getOptions({
          placement: state.placement,
          offset: props.offset,
          autoPlacement: props.autoPlacement,
          preventOverflow: props.preventOverflow,
          autoArrow: props.autoArrow && context.refs.popperArrow
        }));
        popper.value.update();
      }
    }, {
      immediate: true
    });
    return function () {
      var _context$slots$title, _context$slots, _context$slots$defaul, _context$slots2;

      // 判断 受控组件 还是 非受控组件
      var isControlled = typeof props.visible !== 'undefined';
      var visible = isControlled ? props.visible : state.visible;
      var title = props.title || ((_context$slots$title = (_context$slots = context.slots).title) === null || _context$slots$title === void 0 ? void 0 : _context$slots$title.call(_context$slots)) || '';
      var arrow = props.theme === 'light' ? LightArrowMap[state.placement] : DarkArrowMap[state.placement];
      return h("div", [h("div", {
        "ref": "trigger",
        "class": "m-tooltip--trigger",
        "directives": [{
          name: "click-outside",
          value: function value() {
            if (isControlled) {
              context.emit('change', false);
            } else {
              state.visible = false;
            }
          }
        }],
        "on": {
          "click": function click() {
            if (isControlled) {
              context.emit('change', true);
            } else {
              state.visible = true;
            }
          }
        }
      }, [(_context$slots$defaul = (_context$slots2 = context.slots)["default"]) === null || _context$slots$defaul === void 0 ? void 0 : _context$slots$defaul.call(_context$slots2)]), h("div", {
        "ref": "popper",
        "style": props.styles,
        "class": ['m-tooltip', "m-tooltip--theme-".concat(props.theme), visible ? '' : 'hidden', props.tooltipClassName].join(' ')
      }, [h("div", {
        "class": "m-tooltip__content"
      }, [h("div", {
        "class": "m-tooltip__arrow ".concat(state.placement),
        "ref": "popperArrow",
        "style": {
          transform: "translateX(".concat(props.arrowOffset, ")")
        }
      }, [h(arrow)]), h("div", {
        "class": "m-tooltip__name"
      }, [title])])])]);
    };
  }
});
withInstall(ToolTip);
export default ToolTip;